<template>
   <div class="date-picker search__input_wrapper" ref="root">
      <input
         class="search__input"
         v-model="inputValue"
         v-mask="'##.##.####'"
         type="tel"
         :placeholder="placeholder"
         :id="inputId"
         @focus="open = true"
      />
      <label :for="inputId" @click.prevent="toggle">
         <img src="@/assets/img/icons/calendar.svg" alt="" />
      </label>
      <div class="date-picker__dropdown" v-if="open">
         <div class="date-picker__header">
            <button type="button" class="date-picker__nav" @click="prevMonth">
               ‹
            </button>
            <span class="date-picker__title">{{ monthYear }}</span>
            <button type="button" class="date-picker__nav" @click="nextMonth">
               ›
            </button>
         </div>
         <div class="date-picker__weekdays">
            <span v-for="day in weekdays" :key="day">{{ day }}</span>
         </div>
         <div class="date-picker__days">
            <button
               v-for="(day, index) in calendarDays"
               :key="index"
               type="button"
               class="date-picker__day"
               :class="{
                  'date-picker__day--empty': !day,
                  'date-picker__day--selected': day && isSelected(day),
                  'date-picker__day--today': day && isToday(day),
               }"
               :disabled="!day"
               @click="selectDay(day)"
            >
               {{ day ? day.date() : "" }}
            </button>
         </div>
      </div>
   </div>
</template>

<script>
import { mask } from "vue-the-mask";

export default {
   directives: { mask },
};
</script>

<script setup>
import { computed, onMounted, onUnmounted, ref, watch } from "vue";
import moment from "moment";
import "moment/locale/ru";

moment.locale("ru");

const props = defineProps({
   modelValue: {
      type: String,
      default: "",
   },
   placeholder: {
      type: String,
      default: "",
   },
   inputId: {
      type: String,
      default: "",
   },
});

const emit = defineEmits(["update:modelValue"]);

const open = ref(false);
const viewDate = ref(moment());
const inputValue = ref(props.modelValue);
const root = ref(null);

const weekdays = ["Пн", "Вт", "Ср", "Чт", "Пт", "Сб", "Вс"];

const monthYear = computed(() =>
   viewDate.value.clone().locale("ru").format("MMMM YYYY"),
);

const calendarDays = computed(() => {
   const monthStart = viewDate.value.clone().startOf("month");
   const leadingEmpty = monthStart.isoWeekday() - 1;
   const days = [];

   for (let i = 0; i < leadingEmpty; i++) {
      days.push(null);
   }

   for (let day = 1; day <= viewDate.value.daysInMonth(); day++) {
      days.push(monthStart.clone().date(day));
   }

   while (days.length % 7 !== 0) {
      days.push(null);
   }

   return days;
});

watch(
   () => props.modelValue,
   (value) => {
      if (value !== inputValue.value) {
         inputValue.value = value;
      }
      if (value && moment(value, "DD.MM.YYYY", true).isValid()) {
         viewDate.value = moment(value, "DD.MM.YYYY");
      }
   },
   { immediate: true },
);

watch(inputValue, (value) => {
   if (moment(value, "DD.MM.YYYY", true).isValid()) {
      viewDate.value = moment(value, "DD.MM.YYYY");
   }
   emit("update:modelValue", value);
});

function toggle() {
   open.value = !open.value;
}

function prevMonth() {
   viewDate.value = viewDate.value.clone().subtract(1, "month");
}

function nextMonth() {
   viewDate.value = viewDate.value.clone().add(1, "month");
}

function selectDay(day) {
   const formatted = day.format("DD.MM.YYYY");
   inputValue.value = formatted;
   emit("update:modelValue", formatted);
   open.value = false;
}

function isSelected(day) {
   return (
      props.modelValue &&
      moment(props.modelValue, "DD.MM.YYYY", true).isValid() &&
      day.isSame(moment(props.modelValue, "DD.MM.YYYY"), "day")
   );
}

function isToday(day) {
   return day.isSame(moment(), "day");
}

function onClickOutside(e) {
   if (root.value && !root.value.contains(e.target)) {
      open.value = false;
   }
}

onMounted(() => {
   document.addEventListener("click", onClickOutside);
});

onUnmounted(() => {
   document.removeEventListener("click", onClickOutside);
});
</script>

<style scoped>
.date-picker {
   position: relative;
}

.date-picker label {
   position: absolute;
   right: 12px;
   top: 50%;
   transform: translateY(-50%);
   cursor: pointer;
   width: 24px;
   height: 24px;
}

.date-picker label img {
   width: 100%;
   height: 100%;
   object-fit: cover;
}

.date-picker__dropdown {
   position: absolute;
   top: calc(100% + 4px);
   left: 0;
   right: 0;
   z-index: 10;
   background-color: #2d2f33;
   border-radius: 8px;
   padding: 12px;
   box-shadow: 0 8px 24px rgba(0, 0, 0, 0.35);
}

.date-picker__header {
   display: flex;
   align-items: center;
   justify-content: space-between;
   margin-bottom: 12px;
}

.date-picker__title {
   color: #fff;
   font-size: 14px;
   font-weight: 500;
   text-transform: capitalize;
}

.date-picker__nav {
   width: 28px;
   height: 28px;
   border: 0;
   border-radius: 6px;
   background-color: #313337;
   color: #fff;
   font-size: 18px;
   line-height: 1;
   cursor: pointer;
}

.date-picker__nav:hover {
   background-color: #3d4046;
}

.date-picker__weekdays,
.date-picker__days {
   display: grid;
   grid-template-columns: repeat(7, 1fr);
   gap: 4px;
}

.date-picker__weekdays {
   margin-bottom: 8px;
}

.date-picker__weekdays span {
   color: #72757b;
   font-size: 11px;
   text-align: center;
}

.date-picker__day {
   height: 32px;
   border: 0;
   border-radius: 6px;
   background: transparent;
   color: #fff;
   font-size: 13px;
   cursor: pointer;
}

.date-picker__day:hover:not(:disabled) {
   background-color: rgba(255, 255, 255, 0.12);
}

.date-picker__day--empty {
   cursor: default;
}

.date-picker__day--today {
   color: #f5c518;
}

.date-picker__day--selected {
   background-color: #f5c518;
   color: #2d2f33;
}

.date-picker__day--selected:hover {
   background-color: #f5c518;
}
</style>
